import { Ban, ChevronRight, LogIn, Undo2 } from 'lucide-react'
import { MouseEvent, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { toast } from 'sonner'

import { StatusBadge } from '@/components/ds'
import { formatAdminDate, relativeAge } from '@/containers/AdminAccounts/adminFormat'
import API from '@/lib/api'
import { cn } from '@/lib/utils'
import { AdminAccount } from '@/types/Account'

import { Button } from '../ui/button'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'

const stop = (e: MouseEvent) => e.stopPropagation()

export const AdminAccountsTable = ({
  accounts,
  refresh,
}: {
  accounts: AdminAccount[]
  refresh?: () => void
}) => {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'admin.accounts',
  })
  const navigate = useNavigate()
  const [impersonatingAccountId, setImpersonatingAccountId] = useState<string | null>(null)
  const [updatingAccountId, setUpdatingAccountId] = useState<string | null>(null)

  const onImpersonate = (accountId: string) => {
    setImpersonatingAccountId(accountId)

    API.post(`/api/v2/admin/accounts/${accountId}/impersonate`)
      .then(() => {
        window.location.href = '/'
      })
      .catch(() => {
        toast.error(t('list.actions.impersonateError'))
        setImpersonatingAccountId(null)
      })
  }

  const onToggleAbandoned = (account: AdminAccount) => {
    setUpdatingAccountId(account.id)

    API.patch(`/api/v2/admin/accounts/${account.id}`, {
      abandoned: !account.abandoned,
    })
      .then(() => {
        toast.success(t(account.abandoned ? 'abandoned.unmarkSuccess' : 'abandoned.markSuccess'))
        refresh?.()
      })
      .catch(() => {
        toast.error(t('abandoned.error'))
      })
      .finally(() => {
        setUpdatingAccountId(null)
      })
  }

  return (
    <Table>
      <TableHeader>
        <TableRow className="hover:bg-transparent">
          <TableHead>Účet</TableHead>
          <TableHead className="hidden md:table-cell">Vlastník</TableHead>
          <TableHead className="hidden w-44 md:table-cell">Poslední doklad</TableHead>
          <TableHead className="hidden w-36 md:table-cell">Poslední změna</TableHead>
          <TableHead className="w-px md:w-32" />
        </TableRow>
      </TableHeader>
      <TableBody>
        {accounts.map((account) => (
          <TableRow
            key={account.id}
            onClick={() => navigate(`/admin/accounts/${account.id}`)}
            className="group cursor-pointer"
          >
            <TableCell className="w-full max-w-0 md:w-auto">
              <div className="flex items-center gap-2">
                <p className={cn('truncate font-medium', account.abandoned && 'text-muted-foreground')}>{account.name}</p>
                {account.abandoned && <StatusBadge status="draft" label={t('abandoned.badge')} />}
              </div>
              <p className="hidden truncate font-mono text-xs text-muted-foreground md:block">{account.id}</p>
              <p className="truncate text-xs text-muted-foreground md:hidden">
                {[
                  account.ownerId?.fullName ?? account.ownerId?.email,
                  account.lastDocumentCreatedAt ? relativeAge(account.lastDocumentCreatedAt) : 'zatím žádný doklad',
                ]
                  .filter(Boolean)
                  .join(', ')}
              </p>
            </TableCell>
            <TableCell className="hidden max-w-0 md:table-cell">
              <p className="truncate">{account.ownerId?.fullName ?? '-'}</p>
              <p className="truncate text-xs text-muted-foreground">{account.ownerId?.email}</p>
            </TableCell>
            <TableCell className="hidden md:table-cell">
              <p className="tabular">{formatAdminDate(account.lastDocumentCreatedAt)}</p>
              {account.lastDocumentCreatedAt ? (
                <p className="text-xs text-muted-foreground">{relativeAge(account.lastDocumentCreatedAt)}</p>
              ) : (
                <p className="text-xs text-muted-foreground">zatím žádný</p>
              )}
            </TableCell>
            <TableCell className="hidden tabular text-muted-foreground md:table-cell">{formatAdminDate(account.updatedAt)}</TableCell>
            <TableCell onClick={stop}>
              <div className="flex items-center justify-end gap-0.5">
                <div className="flex items-center gap-0.5 transition-opacity md:opacity-0 md:group-hover:opacity-100 md:focus-within:opacity-100">
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={t('list.actions.impersonate')}
                        disabled={impersonatingAccountId === account.id}
                        onClick={() => onImpersonate(account.id)}
                      >
                        <LogIn />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{t('list.actions.impersonate')}</TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        aria-label={account.abandoned ? t('abandoned.unmark') : t('abandoned.mark')}
                        disabled={updatingAccountId === account.id}
                        onClick={() => onToggleAbandoned(account)}
                      >
                        {account.abandoned ? <Undo2 /> : <Ban />}
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>{account.abandoned ? t('abandoned.unmark') : t('abandoned.mark')}</TooltipContent>
                  </Tooltip>
                </div>
                <ChevronRight className="hidden size-4 text-muted-foreground opacity-0 transition-opacity group-hover:opacity-100 md:block" />
              </div>
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  )
}
